import { leaderboardUsers } from "@/models/leaderboardUser.model";
import { ref, child, onValue } from "firebase/database";
import { useState, useEffect, useRef } from "react";

import { getProfileBasedOnUid } from "../utils/helperFunctions/getUserByUid";
import { brand } from "../constants/brandVariables/brandVariables";
import { cacheDatabase } from "../api/firebaseConfig";

type LeaderboardEntry = {
  userId: string;
  userChallengeData: leaderboardUsers["userChallengeData"];
  scanData?: leaderboardUsers["scanData"];
};

type CachedProfile = {
  profile: leaderboardUsers["profile"];
  fetchedAt: number;
};

// Profile changes are rare but possible during long kiosk sessions (12-16h).
// 30-minute TTL refreshes avatars/usernames without hammering Firestore.
const PROFILE_CACHE_TTL_MS = 30 * 60 * 1000;

const PLACEHOLDER_PROFILE = {
  fullName: "",
  slug: "",
  uid: "",
  userName: "- - -",
} as leaderboardUsers["profile"];

export const useRTLeaderboard = (slug: string) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<leaderboardUsers[]>([]);
  const [error, setError] = useState<string>();
  const profileCache = useRef(new Map<string, CachedProfile>());

  useEffect(() => {
    if (!slug) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const leaderboardRef = child(ref(cacheDatabase, "leaderboard"), slug);

    const unsubscribe = onValue(
      leaderboardRef,
      async (snapshot) => {
        if (!snapshot.exists() || !snapshot.val().leaderboard) {
          setData([]);
          setLoading(false);
          return;
        }

        const sortedArray: LeaderboardEntry[] = snapshot.val().leaderboard;
        const now = Date.now();

        const resolved = await Promise.all(
          sortedArray.map(async (val) => {
            const cached = profileCache.current.get(val.userId);
            const isFresh =
              cached && now - cached.fetchedAt < PROFILE_CACHE_TTL_MS;

            let authorProfile = isFresh ? cached.profile : undefined;

            if (!authorProfile) {
              const fetched = await getProfileBasedOnUid(
                val.userId,
                brand.tenantId,
              );
              if (fetched) {
                authorProfile = fetched;
                profileCache.current.set(val.userId, {
                  profile: authorProfile,
                  fetchedAt: now,
                });
              }
            }

            if (authorProfile) {
              return {
                profile: authorProfile,
                userChallengeData: val.userChallengeData,
                scanData: val?.scanData,
              } as leaderboardUsers;
            }
            if (val.userChallengeData) {
              return {
                profile: PLACEHOLDER_PROFILE,
                userChallengeData: val.userChallengeData,
              } as leaderboardUsers;
            }
            return null;
          }),
        );

        const newLeaderboardDataArray = resolved.filter(
          (entry): entry is leaderboardUsers => entry !== null,
        );

        setData(
          newLeaderboardDataArray
            .sort((a, b) => a.userChallengeData.rank - b.userChallengeData.rank)
            .filter((obj) => obj !== undefined && obj !== null),
        );

        setLoading(false);
      },
      (error) => {
        console.error("Error fetching leaderboard:", error);
        setError("Error fetching leaderboard!");
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [slug]);

  return { loading, data, error };
};
